import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import SearchIcon from '../../images/home-landing-page/search-icon.png';
import styles from './SearchForm.module.css';
import { HardHat, Wrench, ShoppingCart, MapPin, Building2 } from 'lucide-react'; 

// 1. PREDEFINED REGIONS (Client's specific request)
const PREDEFINED_REGIONS = [
  { name: "Sydney Metro", postcode: "2000", radius: 40, type: "Region" },
  { name: "Western Sydney", postcode: "2150", radius: 30, type: "Region" },
  { name: "North Shore", postcode: "2060", radius: 20, type: "Region" },
  { name: "Newcastle & Hunter", postcode: "2300", radius: 50, type: "Region" },
  { name: "Wollongong", postcode: "2500", radius: 30, type: "Region" },
];

const SearchFormComponent = ({ className, initialSearchTerm = '' }) => {
  // --- Profession State ---
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [results, setResults] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  
  // --- Location State ---
  const [locationInput, setLocationInput] = useState(''); // Text shown in input
  const [selectedPostCode, setSelectedPostCode] = useState(''); // Value for backend
  const [selectedRadius, setSelectedRadius] = useState(25); // Default radius
  const [locationSuggestions, setLocationSuggestions] = useState([]); // List to display
  const [postCodeError, setPostCodeError] = useState('');

  const navigate = useNavigate();
  const locationWrapperRef = useRef(null); 
  const professionWrapperRef = useRef(null); 

  // --- API: Fetch Professions ---
  const handleProfessionChange = async (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (val.length > 0) {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/SearchLocations/searchAll?search=${val}`);
        setResults(res.data);
      } catch (err) { console.error(err); }
    } else {
      setResults([]);
    }
  };

  const selectProfession = (item) => {
    setSearchTerm(item.name);
    setSelectedType(item.type);
    setResults([]);
  };

  // --- API: Fetch Locations (Suburbs/Postcodes) ---
  const handleLocationChange = async (e) => {
    const val = e.target.value;
    setLocationInput(val);
    setPostCodeError('');
    setSelectedPostCode(''); // Reset selected postcode when typing

    if (val.length < 2) {
        setLocationSuggestions([]);
        return;
    }

    // 1. Filter Predefined Regions (Frontend check)
    const regionMatches = PREDEFINED_REGIONS.filter(r => 
        r.name.toLowerCase().includes(val.toLowerCase())
    );

    // 2. Fetch Suburbs/Postcodes from .NET Backend
    try {
        const res = await axios.get(`${API_BASE_URL}/api/SearchLocations/suggest?query=${val}`);
        const apiSuggestions = res.data.map(item => ({
            name: item.displayName, // e.g. "Paddington, NSW 2021"
            postcode: item.postcode,
            radius: 25, // Default radius for specific suburbs
            type: "Suburb"
        }));

        // Combine: Regions first, then API results
        setLocationSuggestions([...regionMatches, ...apiSuggestions]);

    } catch (error) {
        console.error("Error fetching locations", error);
        // If API fails, just show regions
        setLocationSuggestions(regionMatches);
    }
  };

  const selectLocation = (item) => {
    setLocationInput(item.name); 
    setSelectedPostCode(item.postcode); 
    setSelectedRadius(item.radius); 
    setLocationSuggestions([]); 
  };

  // --- Close Dropdowns on Click Outside ---
  useEffect(() => {
    function handleClickOutside(event) {
        if (locationWrapperRef.current && !locationWrapperRef.current.contains(event.target)) {
            setLocationSuggestions([]);
        }
        if (professionWrapperRef.current && !professionWrapperRef.current.contains(event.target)) {
            setResults([]);
        }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClearProfession = () => {
    setSearchTerm('');
    setResults([]);
  };

  // --- Submit Logic ---
  const handleSearchSubmit = () => {
    // If user typed a 4 digit code directly without clicking dropdown, use it
    let finalPostCode = selectedPostCode;
    
    if (!finalPostCode && /^\d{4}$/.test(locationInput)) {
        finalPostCode = locationInput;
    }

    if (!finalPostCode) {
        setPostCodeError('Please select a valid suburb, postcode, or region.');
        return;
    }

    if (searchTerm.trim() === '') {
        // You might want to allow empty profession search to see all tradies?
        // If not, keep this check.
    }

    //console.log("selectedType = ", selectedType);
    const finalType = selectedType || "Profession";

    // Navigate with parameters
    navigate(`/map-search-v2?searchTerm=${encodeURIComponent(searchTerm)}&postCode=${encodeURIComponent(finalPostCode)}&type=${finalType}&radius=${selectedRadius}`);
  };











  return (
    <div className={`${styles.searchForm} ${styles.searchFormWithoutFilters} ${className}`}>
      <div className={styles.searchInputs}>
        
        {/* === LOCATION INPUT === */}
        <div className={styles.locationInput} ref={locationWrapperRef}>
          <label htmlFor="location-input">Where</label>
          <input
            id="location-input"
            type="text"
            value={locationInput}
            onChange={handleLocationChange}
            placeholder="Suburb, Postcode or Region"
            autoComplete="off"
          />
          {postCodeError && <p className={styles.errorMessage}>{postCodeError}</p>}

          {locationSuggestions.length > 0 && (
            <ul className={styles.dropdownResults}>
                {locationSuggestions.map((item, index) => (
                    <li key={index} className={styles.resultItem} onClick={() => selectLocation(item)}>
                        {item.type === 'Region' ? (
                            <MapPin className={`${styles.icon} ${styles.iconRegion}`} />
                        ) : (
                            <Building2 className={`${styles.icon} ${styles.iconSuburb}`} />
                        )}
                        <span>{item.name}</span>
                    </li>
                ))}
            </ul>
          )}
        </div>

        {/* === PROFESSION INPUT === */}
        <div className={styles.serviceInput} ref={professionWrapperRef}>
          <label htmlFor="service-input">Business type you are looking for</label>
          <input
            id="service-input"
            type="text"
            value={searchTerm}
            onChange={handleProfessionChange}
            placeholder="Electrician or plumber"
            autoComplete="off"
          />
          {searchTerm && (
            <button onClick={handleClearProfession} className={styles.clearButton} aria-label="Clear search term">
              &times;
            </button>
          )}

          {results.length > 0 && (
            <ul className={styles.dropdownResults}>
                {/* Your existing profession grouping logic */}
                {Object.entries(
                    results.reduce((acc, item) => {
                        if (!acc[item.type]) acc[item.type] = [];
                        acc[item.type].push(item);
                        return acc;
                    }, {})
                ).map(([type, items]) => (
                    <React.Fragment key={type}>
                        <li className={styles.categoryHeader}>Select a {type}</li>
                        {items.map((item) => (
                            <li key={`${item.type}-${item.id}`} className={styles.resultItem} onClick={() => selectProfession(item)}>
                                {item.type === 'Profession' && <HardHat className={styles.icon} />}
                                {item.type === 'Service' && <Wrench className={styles.icon} />}
                                {item.type === 'Product' && <ShoppingCart className={styles.icon} />}
                                <span>{item.name}</span>
                            </li>
                        ))}
                    </React.Fragment>
                ))}
            </ul>
          )}
        </div>
      </div>

      <div className={styles.filterSearch}>
        <button type="button" className={styles.searchBtn} onClick={handleSearchSubmit}>
          <img src={SearchIcon} alt="Search" />
          <span>Search</span>
        </button>
      </div>
    </div>
  );
};

export default SearchFormComponent;
